import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pollingIntervalRef = useRef(null);
  const unreadCountPollingIntervalRef = useRef(null);
  const lastCheckRef = useRef(null);
  const isPollingRef = useRef(false);
  const listFetchGenRef = useRef(0);
  const notificationsRef = useRef([]);

  // Load initial notifications and unread count
  // options.silent: if true, don't set loading state (for refetch after mark-all-read)
  const loadNotifications = useCallback(async (params = {}, options = {}) => {
    if (!isAuthenticated) return;

    if (!options.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      // Always add timestamp to bypass cache unless explicitly disabled
      const cacheBypass = params._t === undefined ? { _t: Date.now() } : {};
      const gen = listFetchGenRef.current;
      const response = await notificationService.getNotifications({ ...params, ...cacheBypass });
      if (gen !== listFetchGenRef.current) return;

      const next = response.data || [];
      notificationsRef.current = next;
      setNotifications(next);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      if (!options.silent) setError('Failed to load notifications');
    } finally {
      if (!options.silent) setLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Refetch notifications list and unread count from the server (cache-busting).
   * Use after any read-state mutation so the UI shows server state.
   * options.silent: if true, do not set loading/error (avoids flash).
   */
  const refetchNotifications = useCallback(async (options = {}) => {
    if (!isAuthenticated) return;
    if (!options.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const gen = listFetchGenRef.current;
      const [listResponse, countResponse] = await Promise.all([
        notificationService.getNotifications({ limit: 50, _t: Date.now() }),
        notificationService.getUnreadCount({ _t: Date.now() }),
      ]);
      if (gen !== listFetchGenRef.current) return;
      const next = listResponse.data || [];
      notificationsRef.current = next;
      setNotifications(next);
      setUnreadCount(countResponse.data?.count ?? 0);
    } catch (err) {
      console.error('Failed to refetch notifications:', err);
      if (!options.silent) setError('Failed to load notifications');
    } finally {
      if (!options.silent) setLoading(false);
    }
  }, [isAuthenticated]);

  // Load unread count (now also includes latest notifications)
  const loadUnreadCount = useCallback(async (shouldRefetch = false) => {
    if (!isAuthenticated) return;

    try {
      // Always bypass cache to get fresh data
      const response = await notificationService.getUnreadCount({ _t: Date.now() });
      const newCount = response.data?.count || 0;
      const newNotifications = response.data?.notifications || [];
      const oldCount = unreadCount;

      setUnreadCount(newCount);

      // If unread count increased, immediately invalidate frontend cache and fetch fresh data
      if (newCount > oldCount && oldCount >= 0) {
        // If notifications are included in response, merge them immediately
        // CRITICAL: Preserve optimistic read states when merging
        if (newNotifications.length > 0) {
          setNotifications(prev => {
            const existingMap = new Map(prev.map(n => [n.id, n]));
            const uniqueNew = newNotifications.filter(n => !existingMap.has(n.id));

            // For existing notifications, preserve optimistic read state
            const updated = prev.map(existing => {
              const backend = newNotifications.find(n => n.id === existing.id);
              if (backend) {
                // If existing is optimistically marked as read, preserve that state
                if (existing.is_read && !backend.is_read) {
                  return { ...backend, is_read: true, read_at: existing.read_at };
                }
                return backend; // Use backend data otherwise
              }
              return existing;
            });

            // Prepend new notifications to the list
            const next = [...uniqueNew, ...updated].slice(0, 50); // Keep latest 50
            notificationsRef.current = next;
            return next;
          });
        }

        // ALWAYS force a full reload when count increases to ensure we have all new notifications
        // This bypasses any frontend caching and gets fresh data from backend
        await loadNotifications({ limit: 20, _t: Date.now() });
      } else if (newNotifications.length > 0) {
        // If count didn't increase but we have new notifications, merge them
        // CRITICAL: Preserve optimistic read states - if a notification was optimistically
        // marked as read, don't overwrite it with stale backend data
        setNotifications(prev => {
          const existingMap = new Map(prev.map(n => [n.id, n]));
          const uniqueNew = newNotifications.filter(n => !existingMap.has(n.id));

          // For existing notifications, preserve optimistic read state
          const updated = prev.map(existing => {
            const backend = newNotifications.find(n => n.id === existing.id);
            if (backend) {
              // If existing is optimistically marked as read, preserve that state
              if (existing.is_read && !backend.is_read) {
                return { ...backend, is_read: true, read_at: existing.read_at };
              }
              return backend; // Use backend data otherwise
            }
            return existing;
          });

          return (() => {
            const next = [...uniqueNew, ...updated].slice(0, 50);
            notificationsRef.current = next;
            return next;
          })();
        });
      }
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  }, [isAuthenticated, unreadCount, loadNotifications]);

  // Poll for new notifications
  const pollNotifications = useCallback(async () => {
    if (!isAuthenticated || isPollingRef.current) return;

    isPollingRef.current = true;
    try {
      const lastCheck = lastCheckRef.current ? lastCheckRef.current.toISOString() : null;
      const response = await notificationService.pollNotifications(lastCheck);

      if (response.data) {
        const newNotifications = response.data.notifications || [];
        const newUnreadCount = response.data.unread_count || 0;

        // Update unread count
        const oldUnreadCount = unreadCount;
        setUnreadCount(newUnreadCount);

        // Add new notifications to the list
        if (newNotifications.length > 0) {
          setNotifications(prev => {
            // Merge new notifications, avoiding duplicates
            const existingIds = new Set(prev.map(n => n.id));
            const uniqueNew = newNotifications.filter(n => !existingIds.has(n.id));
            const next = [...uniqueNew, ...prev].slice(0, 50); // Keep latest 50
            notificationsRef.current = next;
            return next;
          });
        } else if (newUnreadCount > oldUnreadCount && oldUnreadCount >= 0) {
          // If unread count increased but no new notifications in poll, refetch full list
          // This handles cases where the notification was created between polls
          loadNotifications({ limit: 20 });
        }

        // Update last check time
        lastCheckRef.current = new Date();
      }
    } catch (err) {
      console.error('Failed to poll notifications:', err);
    } finally {
      isPollingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    listFetchGenRef.current += 1;
    const wasUnread = notificationsRef.current.find(n => n.id === notificationId)?.is_read === false;
    setNotifications(prev => {
      const next = prev.map(n =>
        n.id === notificationId
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n
      );
      notificationsRef.current = next;
      return next;
    });
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await notificationService.markAsRead(notificationId);
      await refetchNotifications({ silent: true });
    } catch (err) {
      setNotifications(prev => {
        const next = prev.map(n =>
          n.id === notificationId ? { ...n, is_read: false, read_at: null } : n
        );
        notificationsRef.current = next;
        return next;
      });
      if (wasUnread) setUnreadCount(prev => prev + 1);
      await refetchNotifications();
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchNotifications]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    listFetchGenRef.current += 1;
    setNotifications(prev => {
      const next = prev.map(n => ({ ...n, is_read: true, read_at: n.read_at || new Date().toISOString() }));
      notificationsRef.current = next;
      return next;
    });
    setUnreadCount(0);

    try {
      await notificationService.markAllAsRead();
      await refetchNotifications({ silent: true });
    } catch (err) {
      await refetchNotifications();
      console.error('Failed to mark all as read:', err);
      throw err;
    }
  }, [refetchNotifications]);

  // Initialize: load notifications and start polling
  useEffect(() => {
    if (!isAuthenticated) {
      notificationsRef.current = [];
      setNotifications([]);
      setUnreadCount(0);
      lastCheckRef.current = null;
      return;
    }

    // Load initial data
    loadUnreadCount();
    loadNotifications({ limit: 20 });

    // Set initial last check time
    lastCheckRef.current = new Date();

    // Start polling for notifications (every 30 seconds)
    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        // Only poll if tab is visible
        if (!document.hidden) {
          pollNotifications();
        }
      }, 30000); // 30 seconds
    };

    // Start polling for unread count (every 10 seconds) - more frequent to catch new notifications quickly
    // This ensures notifications appear immediately when created
    const startUnreadCountPolling = () => {
      if (unreadCountPollingIntervalRef.current) {
        clearInterval(unreadCountPollingIntervalRef.current);
      }

      unreadCountPollingIntervalRef.current = setInterval(() => {
        // Only poll if tab is visible
        if (!document.hidden) {
          loadUnreadCount(true); // Pass true to refetch if count increased
        }
      }, 10000); // 10 seconds - more frequent than notification polling
    };

    startPolling();
    startUnreadCountPolling();

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        // Poll immediately when tab becomes visible
        pollNotifications();
        loadUnreadCount(true); // Also check unread count immediately
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (unreadCountPollingIntervalRef.current) {
        clearInterval(unreadCountPollingIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, loadUnreadCount, loadNotifications, pollNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    loadNotifications,
    loadUnreadCount,
    refetchNotifications,
    pollNotifications,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
